import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from 'react-router-dom';
import { Typography, Form, Spin, Row, Col, Input, Space, Button, Divider, Modal, message, DatePicker, Select } from "antd";
import InputMask from "react-input-mask";
import moment from "moment";
import { CheckCircleOutlined, CloseOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { approveMedicalRecord, getByCustomer, unapproveMedicalRecord, updateMedicalRecord } from "@/redux/actions/medicalRecordActions";
import { checkPermission } from "@/utils/permission";
import { _newLine, genderOptions, customerTypes, ethnicOptions, formatDateForInputMask, isValidDate, occupationOptions, toMoment } from "@/utils/helpers";
import PrintButton from "@/components/PrintButton/PrintButton";
import PrintMedicalRecord from "./PrintMedicalRecord";
import AllergyHistorie from "./AllergyHistorie";
import TreatmentCourseItems from "./TreatmentCourseItems";
import CheckDetailForm from "@/components/CheckDetailForm";
import { getListProvinces, getListWards } from "@/redux/actions/regionsActions";
import BaseSelect from "@/components/Elements/BaseSelect";
import BaseRadioList from "@/components/Elements/BaseRadios";
import { fetchCustomer } from "@/redux/actions/customerActions";

const { Title } = Typography;
const { Option, OptGroup } = Select;

const MedicalRecordForm = (props) => {

    const dispatch = useDispatch();
    const location = useLocation();
    const allowUpdate = checkPermission('medical_records', 'update');
    const allowApproved = checkPermission('medical_records', 'approve');
    const allowUnapproved = checkPermission('medical_records', 'unapprove');

    const { customer } = props;
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [isApproved, setIsApproved] = useState(false);
    const [medicalRecordData, setMedicalRecordData] = useState([]);
    const [wards, setWards] = useState([]);

    const doctors = useSelector((state) => state.config.doctors) || [];
    const sales = useSelector((state) => state.config.sales) || [];
    const config = useSelector((state) => state.config);
    const provinces = useSelector((state) => state.regionsReducer.list_provinces);

    useEffect(() => {
        if (customer?.province_code) {
            loadWards(customer?.province_code);
        }
    }, [customer?.province_code]);

    useEffect(() => {
        dispatch(getListProvinces());
    }, [form]);

    // Tạo defaultValues
    const defaultValues = useMemo(() => ({
        customer_name: customer?.full_name,
        customer_code: customer?.code,
        birthday: formatDateForInputMask(customer?.birthday, false),
        age: customer?.age,
        gender: customer?.gender,
        occupation: customer?.occupation,
        ethnicity: customer?.ethnicity,
        nationality: customer?.nationality || "Việt Nam",
        province_code: customer?.province_code,
        ward_code: customer?.ward_code,
        hamlet: customer?.hamlet,
        address: customer?.address,
        customer_type: customer?.customer_type,
        bhyt_number: customer?.bhyt_number,
        identity_number: customer?.identity_number,
        relative_info: customer?.relative_info,
        relative_info_phone: customer?.relative_info_phone,
        note: _newLine(customer?.note),
        bac_si_phu_trach: null,
        sales_ids: [],
        patient_allergy_info: [
            { content: "Loại thuốc hoặc dị nguyên nào đã gây ra dị ứng?", allergen_name: "", occurrence_count: "", has_occurrence: "Không", clinical_signs: "", isNew: false },
            { content: "Dị ứng với loại côn trùng nào?", allergen_name: "", occurrence_count: "", has_occurrence: "Không", clinical_signs: "", isNew: false },
            { content: "Dị ứng với loại thực phẩm nào?", allergen_name: "", occurrence_count: "", has_occurrence: "Không", clinical_signs: "", isNew: false },
            { content: "Dị ứng với các tác nhân nào: phấn hoa, bụi nhà, hóa chất, mỹ phẩm...?", allergen_name: "", occurrence_count: "", has_occurrence: "Không", clinical_signs: "", isNew: false },
            { content: "Tiền sử cá nhân có bệnh dị ứng nào? (viêm mũi dị ứng, hen phế quản...)", allergen_name: "", occurrence_count: "", has_occurrence: "Không", clinical_signs: "", isNew: false },
            { content: "Tiền sử gia đình có bệnh dị ứng nào? (Bố mẹ, con, anh chị em ruột, có ai bị các bệnh dị ứng trên không)", allergen_name: "", occurrence_count: "", has_occurrence: "Không", clinical_signs: "", isNew: false }
        ],
        qua_trinh_dieu_tri: [
            {
                ngay: moment(),
                dien_bien_benh: '',
                xu_tri: '',
                note: ''
            }
        ],
    }), [customer]);

    const loadMedicalecords = useCallback(async () => {
        if (!customer?.id) return;
        setLoading(true);

        try {
            const result = await dispatch(getByCustomer({}, customer.id));
            //console.log('result', result);
            const data = result?.data;

            if (data && Object.keys(data).length > 0) {
                const normalized = {
                    ...data,
                    // Chuẩn hóa các trường ngày đơn
                    thoi_gian_dieu_tri_start: toMoment(data.thoi_gian_dieu_tri_start),
                    thoi_gian_dieu_tri_end: toMoment(data.thoi_gian_dieu_tri_end),

                    // Chuẩn hóa mảng quá trình điều trị (nếu có)
                    qua_trinh_dieu_tri: Array.isArray(data.qua_trinh_dieu_tri)
                        ? data.qua_trinh_dieu_tri.map(item => ({
                            ...item,
                            ngay: toMoment(item.ngay),
                        }))
                        : defaultValues.qua_trinh_dieu_tri,
                };

                setMedicalRecordData(normalized);
                setIsApproved(!!data.is_approved);
            } else {
                setMedicalRecordData(defaultValues);
                setIsApproved(false);
            }
        } catch (error) {
            console.error("Failed to load medical records:", error);
            setMedicalRecordData(defaultValues);
            setIsApproved(false);
        } finally {
            setLoading(false);
        }
    }, [dispatch, customer?.id]);


    useEffect(() => {
        loadMedicalecords();
    }, [location.pathname]);

    const onFinish = useCallback(
        async (values) => {
            if (!customer?.id) return;
            //console.log("values", values);
            setLoading(true);
            try {
                const formatted = {
                    ...values
                };

                console.log("formatted", formatted);

                await dispatch(updateMedicalRecord(customer.id, formatted));
                await dispatch(fetchCustomer(customer.id));
                loadMedicalecords();
            } finally {
                setLoading(false);
            }
        },
        [dispatch, customer?.id]
    );

    const onFinishFailed = (err) => {
        message.error('Vui lòng điền đầy đủ các trường bắt buộc');
    };

    const handleBirthdayChange = (e) => {
        const value = e.target.value;
        const date = moment(value, "DD/MM/YYYY", true);

        if (date.isValid()) {
            const age = moment().diff(date, "years");
            form.setFieldsValue({ age });
        } else {
            form.setFieldsValue({ age: "" });
        }
    };

    const handleApprove = async () => {
        if (!medicalRecordData?.id) {
            message.error("Chưa có bệnh án tổng quát để xác nhận");
            return;
        }

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn xác nhận bệnh án tổng quát?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setLoading(true);
                try {
                    await dispatch(approveMedicalRecord(medicalRecordData.id));
                    await loadMedicalecords(); // refresh lại data
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleUnapprove = async () => {
        if (!medicalRecordData?.id) return;

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn hủy xác nhận phiếu khám tổng quát?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setLoading(true);
                try {
                    await dispatch(unapproveMedicalRecord(medicalRecordData.id));
                    await loadMedicalecords(); // refresh lại data
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    useEffect(() => {
        if (!defaultValues) return;

        // Ưu tiên dữ liệu từ DB, còn thiếu thì lấy từ defaultValues
        // Mục đích: giúp chọn được nhiều radio độc lập trong danh sách bệnh (diseases)
        const mergedData = {
            ...defaultValues,
            ...medicalRecordData
        };

        form.resetFields();
        form.setFieldsValue(mergedData);
    }, [medicalRecordData, defaultValues, form]);

    const loadWards = async (province_code, reset = null) => {
        if (province_code) {
            try {
                const res = await dispatch(getListWards({ province_code })); // Gọi action
                if (reset) {
                    form.setFieldsValue({ ward_code: undefined });
                }
                setWards(res.data);
            } catch (error) {
                console.error("Lỗi khi tải danh sách phường/xã:", error);
            }
        }
    };

    const countryOptions = useMemo(() => {
        return config?.countries
            ? Object.entries(config.countries).map(([code, name]) => ({
                value: code,
                label: name,
            }))
            : [];
    }, [config?.countries]);


    return (
        <div>
            <div style={{
                textAlign: "right",
            }} className="mt-3 mb-3">
                <Space>
                    <PrintButton printText="In bệnh án tổng quát">
                        <PrintMedicalRecord
                            customer={customer}
                            data={medicalRecordData}
                        />
                    </PrintButton>
                </Space>
            </div>
            <Spin spinning={loading}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    disabled={isApproved}
                    initialValues={defaultValues}
                    className="form-readonly-custom-bg vertical-custom-item"
                >
                    <Divider orientation="left" orientationMargin="0" className='mb-0'>A. THÔNG TIN CHUNG</Divider>
                    <Row gutter={8} className="mt-3 mb-3">
                        <Col span={6}>
                            <Form.Item
                                label="Tên khách hàng"
                                name="customer_name"
                                rules={[{ required: true, message: "Trường bắt buộc!" }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Mã khách hàng"
                                name="customer_code"
                                rules={[{ required: true, message: "Trường bắt buộc!" }]}
                            >
                                <Input disabled />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Số căn cước"
                                name="identity_number"
                                rules={[{ required: false, message: "Bắt buộc chọn" }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Ngày sinh"
                                        name="birthday"
                                        rules={[{ required: true, message: "Trường bắt buộc!" }]}
                                    >
                                        <InputMask
                                            mask="99/99/9999"
                                            placeholder="dd/mm/yyyy"
                                            onChange={e => {
                                                handleBirthdayChange(e);
                                            }}
                                        >
                                            {(inputProps) => <Input {...inputProps} />}
                                        </InputMask>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Tuổi"
                                        name="age"
                                        rules={[{ required: true, message: "Trường bắt buộc!" }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Giới tính"
                                name="gender"
                                rules={[{ required: true, message: "Bắt buộc chọn" }]}
                            >
                                <BaseRadioList
                                    options={genderOptions || []}
                                    name="gender"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Nghề nghiệp"
                                name="occupation"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Chọn ngành nghề"
                                    optionFilterProp="label"
                                    style={{ width: '100%' }}
                                    allowClear
                                >
                                    {occupationOptions.map((group) => (
                                        <OptGroup key={group.group} label={group.group}>
                                            {group.options.map((item) => (
                                                <Option key={item.value} value={item.value} label={item.label}>
                                                    {item.label}
                                                </Option>
                                            ))}
                                        </OptGroup>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Dân tộc"
                                name="ethnicity"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                            >
                                <BaseSelect
                                    options={ethnicOptions || []}
                                    optionValue="value"
                                    optionLabel="label"
                                    showSearch
                                    placeholder="Chọn một bản ghi"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={6}>
                            <Form.Item
                                label="Quốc tịch"
                                name="nationality"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                            >
                                <BaseSelect
                                    options={countryOptions || []}
                                    optionValue="value"
                                    optionLabel="label"
                                    showSearch
                                    placeholder="Chọn một bản ghi"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Tỉnh, thành phố"
                                name="province_code"
                                rules={[{ required: false, message: "Bắt buộc chọn" }]}
                            >
                                <BaseSelect
                                    options={provinces || []}
                                    optionValue="code"
                                    optionLabel="name"
                                    showSearch
                                    placeholder="Chọn một bản ghi"
                                    style={{ width: '100%' }}
                                    onChange={(code) => loadWards(code, true)}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Xã, phường"
                                name="ward_code"
                                rules={[{ required: false, message: "Bắt buộc chọn" }]}
                            >
                                <BaseSelect
                                    options={wards || []}
                                    optionValue="code"
                                    optionLabel="name"
                                    showSearch
                                    placeholder="Chọn một bản ghi"
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Thôn, phố"
                                name="hamlet"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                label="Địa chỉ"
                                name="address"
                                rules={[{ required: false, message: "Trường bắt buộc!" }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={12}>
                            <Form.Item
                                label="Đối tượng"
                                name="customer_type"
                                rules={[{ required: false, message: "Bắt buộc chọn" }]}
                            >
                                <BaseRadioList
                                    options={customerTypes || []}
                                    optionValue="value"
                                    optionLabel="label"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Số thẻ BHYT"
                                name="bhyt_number"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={18}>
                            <Form.Item
                                label="Họ và tên thân nhân của NB khi cần báo tin"
                                name="relative_info"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Điện thoại"
                                name="relative_info_phone"
                                rules={[
                                    { required: false, message: "Bắt buộc nhập" }
                                ]}
                            >
                                <Input placeholder="Nhập số điện thoại" />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                label="Ghi chú"
                                name="note"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Bác sĩ phụ trách"
                                name="bac_si_phu_trach"
                                rules={[{ required: false, message: "Trường bắt buộc!" }]}
                            >
                                <BaseSelect
                                    options={doctors || []}
                                    optionValue="user_id"
                                    optionLabel="display_name"
                                    showSearch
                                    placeholder="Chọn bác sĩ"
                                    className="w-100"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={16}>
                            <Form.Item
                                label="Nhân viên sales"
                                name="sales_ids"
                                rules={[{ required: false, message: "Trường bắt buộc!" }]}
                            >
                                <BaseSelect
                                    options={sales || []}
                                    optionValue="user_id"
                                    optionLabel="display_name"
                                    showSearch
                                    mode="multiple"
                                    placeholder="Chọn một hoặc nhiều nhân viên sales"
                                    className="w-100"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left" orientationMargin="0" className='mt-4 mb-0'>PHIẾU KHAI THÁC TIỀN SỬ DỊ ỨNG</Divider>
                    <Row gutter={8}>
                        <Col span={24}>
                            <AllergyHistorie form={form} />
                        </Col>
                    </Row>

                    <Divider orientation="left" orientationMargin="0" className='mt-3 mb-3'>B. THÔNG TIN KHÁM BỆNH</Divider>
                    <Title level={5} className="section-title-sm">I. LÝ DO VÀO VIỆN, VẤN ĐỀ SỨC KHỎE</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="ly_do_vao_vien"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Title className='mt-3 section-title-sm' level={5}>II. HỎI BỆNH</Title>
                    <Title level={5} strong className="mt-3 section-title-sm">1. Quá trình bệnh lý và diễn biến lâm sàng <span className='ant-form-item-extra-custom'>(Đặc điểm khởi phát, các triệu chứng lâm sàng, diễn biến bệnh...)</span></Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="hoi_benh"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Title level={5} strong className="mt-3 section-title-sm">2. Tiền sử có liên quan đến tình trạng bệnh lần này:</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <CheckDetailForm
                                style={{ marginBottom: "5px" }}
                                listName="tien_su_benh_lien_quan"
                                data={[
                                    {
                                        label: "Dị ứng",
                                        inputType: "rank",
                                        options: [
                                            { value: "Không", label: "Không" },
                                            { value: "Có", label: "Có (ghi rõ chi tiết)" }
                                        ],
                                        triggerValues: ["Có"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                    },
                                    {
                                        label: "Bản thân",
                                        inputType: "rank",
                                        options: [
                                            { value: "Không", label: "Không" },
                                            { value: "Có", label: "Có (ghi rõ chi tiết)" }
                                        ],
                                        triggerValues: ["Có"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                    },
                                    {
                                        label: "Gia đình",
                                        inputType: "rank",
                                        options: [
                                            { value: "Không", label: "Không" },
                                            { value: "Có", label: "Có (ghi rõ chi tiết)" }
                                        ],
                                        triggerValues: ["Có"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                    },
                                ]}
                                columns={[
                                    {
                                        title: "",
                                        dataIndex: "label",
                                        width: "100px",
                                        render: (_, record, index) => (
                                            <>
                                                <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                    <Input />
                                                </Form.Item>
                                            </>
                                        ),
                                    },
                                ]}
                                requiredColWidth="300px"
                            />
                        </Col>
                    </Row>
                    <Title className='mt-3 section-title-sm' level={5}>III. KHÁM BỆNH</Title>
                    {/* <Title level={5} className="mt-1 section-title-sm">Các chỉ số sinh tồn</Title> */}
                    <Row gutter={8}>
                        <Col xs={24} md={4}>
                            <Form.Item
                                label="Mạch (lần/phút)"
                                name="mach"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input placeholder='lần/phút' />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                            <Form.Item
                                label="Nhiệt độ (°C)"
                                name="nhiet_do"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input placeholder='°C' />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                            <Form.Item
                                label="Huyết áp (.../...mmHg)"
                                name="huyet_ap"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input placeholder='.../...mmHg' />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                            <Form.Item
                                label="Nhịp thở (lần/phút)"
                                name="nhip_tho"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input placeholder='.....lần/phút' />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                            <Form.Item
                                label="Cân nặng (kg)"
                                name="can_nang"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input placeholder='kg' />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={4}>
                            <Form.Item
                                label="Chiều cao (cm)"
                                name="chieu_cao"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input placeholder='cm' />
                            </Form.Item>
                        </Col >
                    </Row >
                    <Title level={5} strong className="mt-1 section-title-sm">1. Toàn thân</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <CheckDetailForm
                                style={{ marginBottom: "5px" }}
                                listName="toan_than"
                                data={[
                                    {
                                        inputType: "rank",
                                        options: [
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Bất thường", label: "Bất thường (ghi rõ chi tiết)" }
                                        ],
                                        triggerValues: ["Bất thường"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                    }
                                ]}
                                requiredColWidth="400px"
                            />
                        </Col>
                    </Row>
                    <Title level={5} strong className="mt-1 section-title-sm">2. Khám chuyên khoa</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <CheckDetailForm
                                style={{ marginBottom: "5px" }}
                                listName="kham_chuyen_khoa"
                                data={[
                                    {
                                        label: "Ngoài miệng",
                                        inputType: "rank",
                                        options: [
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Bất thường", label: "Bất thường (ghi rõ chi tiết)" }
                                        ],
                                        triggerValues: ["Bất thường"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                    },
                                    {
                                        label: "Trong miệng",
                                        inputType: "rank",
                                        options: [
                                            { value: "Bình thường", label: "Bình thường" },
                                            { value: "Bất thường", label: "Bất thường (ghi rõ chi tiết)" }
                                        ],
                                        triggerValues: ["Bất thường"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                    }
                                ]}
                                columns={[
                                    {
                                        title: "",
                                        dataIndex: "label",
                                        width: "150px",
                                        render: (_, record, index) => (
                                            <>
                                                <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                    <Input />
                                                </Form.Item>
                                            </>
                                        ),
                                    },
                                ]}
                                requiredColWidth="400px"
                            />
                        </Col>
                    </Row>
                    <Title level={5} strong className="mt-1 section-title-sm">3. Các xét nghiệm, cận lâm sàng cần làm</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <CheckDetailForm
                                style={{ marginBottom: "5px" }}
                                listName="cac_xet_nghiem_can_lam"
                                data={[
                                    {
                                        inputType: "rank",
                                        options: [
                                            { value: "Không", label: "Không" },
                                            { value: "Có", label: "Có (ghi rõ chi tiết)" }
                                        ],
                                        triggerValues: ["Có"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                    }
                                ]}
                                requiredColWidth="300px"
                            />
                        </Col>
                    </Row>
                    <Title level={5} strong className="mt-1 section-title-sm">4. Tóm tắt bệnh án</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="tom_tat_benh_an"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Title className='mt-3 section-title-sm' level={5}>IV. CHẨN ĐOÁN (Tên bệnh kèm theo mã ICD)</Title>
                    <Title level={5} strong className="mt-2 section-title-sm">Bệnh chính</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="benh_chinh"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                style={{ display: 'inline-block', width: 'calc(70% - 8px)' }}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="benh_chinh_code"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                style={{ display: 'inline-block', width: 'calc(30% - 8px)', margin: '0 8px' }}
                            >
                                <Input placeholder='Mã ICD' />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Title level={5} strong className="mt-2 section-title-sm">Bệnh kèm theo (nếu có)</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="benh_kem_theo"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                style={{ display: 'inline-block', width: 'calc(70% - 8px)' }}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="benh_kem_theo_code"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                style={{ display: 'inline-block', width: 'calc(30% - 8px)', margin: '0 8px' }}
                            >
                                <Input placeholder='Mã ICD' />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Title className='mt-2 section-title-sm' level={5}>Biến chứng (nếu có)</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="bien_chung"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                style={{ display: 'inline-block', width: 'calc(70% - 8px)' }}
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item
                                name="bien_chung_code"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                                style={{ display: 'inline-block', width: 'calc(30% - 8px)', margin: '0 8px' }}
                            >
                                <Input placeholder='Mã ICD' />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Title className='mt-3 section-title-sm' level={5}>V. KẾ HOẠCH ĐIỀU TRỊ</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="ke_hoach_dieu_tri"
                                rules={[{ required: false, message: "Bắt buộc nhập" }]}
                            >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row >
                    <Title className='mt-3 section-title-sm' level={5}>VI. QUÁ TRÌNH ĐIỀU TRỊ</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <TreatmentCourseItems form={form} />
                        </Col>
                    </Row>
                    <Title className='mt-3 section-title-sm' level={5}>VII. THỜI GIAN ĐIỀU TRỊ</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={6}>
                            <Form.Item
                                label="Từ ngày"
                                name="thoi_gian_dieu_tri_start"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    placeholder="dd/mm/yyyy"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item
                                label="Đến ngày"
                                name="thoi_gian_dieu_tri_end"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    placeholder="dd/mm/yyyy"
                                />
                            </Form.Item>
                        </Col >
                    </Row >

                    <Divider orientation="left" orientationMargin="0" className='mt-4 mb-0'>C. TỔNG KẾT BỆNH ÁN</Divider>
                    <Title className='section-title-sm mt-3' level={5}>1. Tóm tắt quá trình bệnh lý và diễn biến lâm sàng <span className='ant-form-item-extra-custom'>(Đặc điểm khởi phát, các triệu chứng lâm sàng, diễn biến bệnh...)</span></Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="tom_tat_qua_trinh_benh_ly"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Title className='mt-3 section-title-sm' level={5}>2. Những dấu hiệu lâm sàng chính được ghi nhận
                        <span className='ant-form-item-extra-custom'>(có giá trị chẩn đoán trong quá trình điều trị)</span>
                    </Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="dau_hieu_lam_sang"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Title className='mt-3 section-title-sm' level={5}>3. Tóm tắt kết quả xét nghiệm, cận lâm sàng có giá trị chẩn đoán</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="tom_tat_ket_qua_xet_nghiem"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input.TextArea rows={4} />
                            </Form.Item>
                        </Col >
                    </Row>

                    <Title className='mt-3 section-title-sm' level={5}>4. Phương pháp điều trị (tương ứng với chẩn đoán)</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <CheckDetailForm
                                style={{ marginBottom: "5px" }}
                                listName="phuong_phap_dieu_tri"
                                data={[
                                    {
                                        label: "Nội khoa",
                                        inputType: "rank",
                                        options: [
                                            { value: "Không", label: "Không" },
                                            { value: "Có", label: "Có (ghi rõ chi tiết)" }
                                        ],
                                        triggerValues: ["Có"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                    },
                                    {
                                        label: "Phẫu thuật, thủ thuật",
                                        inputType: "rank",
                                        options: [
                                            { value: "Không", label: "Không" },
                                            { value: "Có", label: "Có (ghi rõ chi tiết)" }
                                        ],
                                        triggerValues: ["Có"], // chỉ hiển thị TextArea nếu chọn Hạng 3
                                    }
                                ]}
                                columns={[
                                    {
                                        title: "",
                                        dataIndex: "label",
                                        width: "180px",
                                        render: (_, record, index) => (
                                            <>
                                                <span style={{ fontWeight: 500 }} className="check-detail-label">{record.label}</span>
                                                <Form.Item name={[index, "label"]} initialValue={record.label} hidden>
                                                    <Input />
                                                </Form.Item>
                                            </>
                                        ),
                                    },
                                ]}
                                placeholder="Nhập phương pháp điều trị"
                                requiredColWidth="300px"
                            />
                        </Col>
                    </Row>

                    <Title className='mt-3 section-title-sm' level={5}>5. Tình trạng ra viện</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="tinh_trang_ra_vien"
                                rules={[{ required: false, message: 'Bắt buộc chọn' }]}
                            >
                                {/* <Checkbox.Group
                                    options={[
                                        { label: 'Khỏi', value: 'Khỏi' },
                                        { label: 'Đỡ', value: 'Đỡ' },
                                        { label: 'Không thay đổi', value: 'Không thay đổi' },
                                        { label: 'Nặng hơn', value: 'Nặng hơn' },
                                        { label: 'Tử vong', value: 'Tử vong' },
                                        { label: 'Tiên lượng nặng xin về', value: 'Tiên lượng nặng xin về' },
                                        { label: 'Khác', value: 'Khác' }
                                    ]}
                                /> */}
                                <BaseRadioList
                                    //options={customerTypes || []}
                                    options={[
                                        { label: 'Khỏi', value: 'Khỏi' },
                                        { label: 'Đỡ', value: 'Đỡ' },
                                        { label: 'Không thay đổi', value: 'Không thay đổi' },
                                        { label: 'Nặng hơn', value: 'Nặng hơn' },
                                        { label: 'Tử vong', value: 'Tử vong' },
                                        { label: 'Tiên lượng nặng xin về', value: 'Tiên lượng nặng xin về' },
                                        { label: 'Khác', value: 'Khác' }
                                    ]}
                                    optionValue="value"
                                    optionLabel="label"
                                />
                            </Form.Item>
                        </Col >
                    </Row>

                    <Title className='mt-3 section-title-sm' level={5}>6. Hướng điều trị và các chế độ tiếp theo</Title>
                    <Row gutter={8}>
                        <Col xs={24} md={24}>
                            <Form.Item
                                name="huong_dieu_tri_tiep_theo"
                                rules={[{ required: false, message: 'Bắt buộc nhập' }]}
                            >
                                <Input.TextArea rows={6} />
                            </Form.Item>
                        </Col >
                    </Row >
                    <div
                        style={{
                            // marginTop: "30px",
                            // position: "sticky",
                            bottom: 0,
                            background: "#fff",
                            padding: "12px 0",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            zIndex: 100,
                            borderTop: "1px solid #f0f0f0",
                        }}
                    >
                        {/* Nút bên trái */}
                        <div>
                            {isApproved ? (
                                <>
                                    {allowUnapproved && (
                                        <Button
                                            htmlType="button"
                                            icon={<CloseOutlined />}
                                            onClick={handleUnapprove}
                                            danger
                                            size="large"
                                            disabled={false}
                                        >
                                            Hủy xác nhận của bác sĩ
                                        </Button>
                                    )}
                                    <div className="mt-2" style={{ color: 'rgba(0, 0, 0, 0.45)', fontStyle: 'italic' }}>
                                        Xác nhận bởi: {medicalRecordData?.approved_by?.full_name} -{" "}
                                        {isValidDate(medicalRecordData?.approved_at, true)}
                                    </div>
                                </>
                            ) : (
                                allowApproved && (
                                    <Button
                                        type="primary"
                                        style={{ background: "#52c41a", borderColor: "#52c41a" }}
                                        icon={<CheckCircleOutlined />}
                                        onClick={handleApprove}
                                        size="large"
                                        disabled={false}
                                    >
                                        Xác nhận của bác sĩ
                                    </Button>
                                )
                            )}
                        </div>


                        {/* Nhóm nút bên phải */}
                        {allowUpdate &&
                            <Space>
                                <Button
                                    htmlType="button"
                                    size="large"
                                    onClick={() => form.resetFields()}
                                    icon={<ReloadOutlined />}
                                >
                                    Reset
                                </Button>
                                <Button
                                    type="primary"
                                    size="large"
                                    htmlType="submit"
                                    loading={loading}
                                    icon={<SaveOutlined />}
                                >
                                    Lưu
                                </Button>

                            </Space>}
                    </div>

                </Form>
            </Spin >
        </div >
    );
};

export default MedicalRecordForm;
